/**
 * RHDiagnosticoDetalhe V55 — Drill-down de uma avaliação (setor/cargo)
 * URL: /dashboards/rh/diagnostico/:id
 * O :id é usado como companyId; sector e jobTitle vêm de query params
 */
import SalviaLayout from "@/components/SalviaLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import { useParams, Link, useSearch } from "wouter";
import { RadarChart, InfoTooltip } from "@/components/home";

export default function RHDiagnosticoDetalhe() {
  usePageTitle("Detalhe da Avaliação");
  const { user } = useAuth();
  const params = useParams<{ id: string }>();
  const searchStr = useSearch();
  const sp = new URLSearchParams(searchStr);
  const sector = sp.get("sector") || "";
  const jobTitle = sp.get("jobTitle") || "";
  const companyId = Number(params.id);

  const { data, isLoading, error } = trpc.dashboardsAdv.rh.evaluationDetail.useQuery(
    { companyId, sector, jobTitle },
    { enabled: !!user && !isNaN(companyId) && !!sector && !!jobTitle, refetchOnWindowFocus: false }
  );

  if (error) {
    return (
      <SalviaLayout title="Detalhe da Avaliação">
        <Card className="p-8 text-center">
          <AlertTriangle className="mx-auto mb-4 text-destructive" size={48} />
          <p className="text-muted-foreground">{error.message}</p>
        </Card>
      </SalviaLayout>
    );
  }

  const loading = isLoading || !data;
  const d = data as any;

  return (
    <SalviaLayout title={`Avaliação — ${sector} / ${jobTitle}`}>
      <div className="space-y-6">
        <Link href="/dashboards/rh/diagnostico" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={16} /> Voltar para Diagnóstico
        </Link>

        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-32 w-full" />)}</div>
        ) : !d.meetsKMin ? (
          <Card className="p-8 text-center">
            <AlertTriangle className="mx-auto mb-4 text-amber-500" size={48} />
            <h3 className="font-semibold text-lg mb-2">Dados Protegidos (kMin)</h3>
            <p className="text-muted-foreground">
              Esta avaliação possui poucos respondentes, abaixo do mínimo necessário.
              Os dados individuais estão protegidos para garantir anonimato.
            </p>
          </Card>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Card className="p-4 text-center">
                <div className="text-2xl font-bold tabular-nums">{d.kpis?.respondentCount}</div>
                <div className="text-xs text-muted-foreground">Respondentes</div>
              </Card>
              <Card className="p-4 text-center">
                <div className="text-2xl font-bold tabular-nums">{d.kpis?.classificationScore?.toFixed(1)}</div>
                <div className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                  Score Ergos
                  <InfoTooltip label="" formula="Score = Σ(respostas × peso)" description="0 a 25" />
                </div>
              </Card>
              <Card className="p-4 text-center">
                <div className="text-sm font-bold">{d.kpis?.qualifier || "—"}</div>
                <div className="text-xs text-muted-foreground">Qualificador</div>
              </Card>
              <Card className="p-4 text-center">
                <div className="text-sm font-bold">{d.kpis?.classificationLabel || "—"}</div>
                <div className="text-xs text-muted-foreground">Classificação P×S</div>
              </Card>
            </div>

            {/* Radar */}
            {d.radarData && d.radarData.length >= 3 && (
              <Card className="p-6">
                <RadarChart data={d.radarData} title="Radar de Dimensões" />
              </Card>
            )}

            {/* Modules Ranking */}
            {d.modules && d.modules.length > 0 && (
              <Card className="p-6">
                <h3 className="text-sm font-semibold text-[#002F59] mb-4">Ranking de Módulos</h3>
                <div className="space-y-2">
                  {d.modules.map((m: any, i: number) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs font-mono text-muted-foreground w-6">{i + 1}.</span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">{m.moduleName}</span>
                          <span className="text-xs tabular-nums text-muted-foreground">{m.moduleScore?.toFixed(1)}</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-red-500" style={{ width: `${Math.min((m.moduleScore / 25) * 100, 100)}%` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Questions Detail */}
            {d.questions && d.questions.length > 0 && (
              <Card className="p-6">
                <h3 className="text-sm font-semibold text-[#002F59] mb-4">Detalhe por Pergunta</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Código</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Módulo</th>
                        <th className="px-3 py-3 text-right text-xs font-semibold text-muted-foreground uppercase">Valor Moda</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Label</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.questions.map((q: any, i: number) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-3 py-2.5 font-mono text-xs">{q.questionCode}</td>
                          <td className="px-3 py-2.5">{q.moduleCode}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{q.modeValue}</td>
                          <td className="px-3 py-2.5">{q.modeLabel || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </SalviaLayout>
  );
}
